import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SyncStage, SyncProgressResponse } from '@loader/shared-types';

import { useTriggerCustomSync } from './useCustomConnectors';
import { useToggleConnector } from './useBaseConnectors';

import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';
import { useToastStore } from '@/store';

/** Safety valve: clear optimistic syncing state after 5 min (real state comes from progress poll) */
const SYNC_INDICATOR_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Shared hook for triggering syncs with optimistic syncing indicators.
 *
 * Encapsulates the syncing state, timer management, and cleanup logic
 * used by ConnectorTypeGroup and native connection adapters.
 */
export function useSyncTrigger() {
  const triggerSync = useTriggerCustomSync();
  const toast = useToastStore();
  const [syncingTableIds, setSyncingTableIds] = useState<Set<string>>(new Set());
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers) clearTimeout(timer);
    };
  }, []);

  const handleTriggerSync = useCallback((tableId: string) => {
    setSyncingTableIds((prev) => new Set(prev).add(tableId));
    triggerSync.mutate(tableId, {
      onSuccess: () => {
        toast.success('Sync triggered - new data will appear as a new version');
        const timer = setTimeout(() => {
          timersRef.current.delete(timer);
          setSyncingTableIds((prev) => {
            const next = new Set(prev);
            next.delete(tableId);
            return next;
          });
        }, SYNC_INDICATOR_TIMEOUT_MS);
        timersRef.current.add(timer);
      },
      onError: () => {
        toast.error('Failed to trigger sync');
        setSyncingTableIds((prev) => {
          const next = new Set(prev);
          next.delete(tableId);
          return next;
        });
      },
    });
  }, [triggerSync, toast]);

  return { handleTriggerSync, syncingTableIds };
}

/**
 * Shared hook for pausing/resuming connectors via toggle.
 *
 * Encapsulates the toggleConnector mutation with consistent toast messages,
 * used by ConnectorTypeGroup and native connection adapters.
 */
export function useConnectorToggle() {
  const toggleConnector = useToggleConnector();
  const toast = useToastStore();

  const handlePause = useCallback(
    (tableId: string) => {
      toggleConnector.mutate(
        { tableId, enabled: false },
        {
          onSuccess: () => toast.success('Connector paused'),
          onError: () => toast.error('Failed to pause connector'),
        },
      );
    },
    [toggleConnector, toast],
  );

  const handleResume = useCallback(
    (tableId: string) => {
      toggleConnector.mutate(
        { tableId, enabled: true },
        {
          onSuccess: () => toast.success('Connector resumed'),
          onError: () => toast.error('Failed to resume connector'),
        },
      );
    },
    [toggleConnector, toast],
  );

  return { handlePause, handleResume };
}

/** Poll interval for sync progress */
const SYNC_PROGRESS_POLL_MS = 3_000;

export interface SyncProgressInfo {
  recordsFetched: number;
  totalRecords: number | null;
  stage: SyncStage;
}

export interface SyncProgressResult {
  /** Backend confirms a processing/queued version exists */
  syncing: boolean;
  /** Row-level progress data (null if no rows fetched yet) */
  progress: SyncProgressInfo | null;
}

/**
 * Poll live fetch progress for a connector sync.
 *
 * Always fetches on mount to detect in-flight syncs (survives navigation).
 * Polls every 3s while syncing (optimistic trigger OR backend confirms processing version).
 * Stops polling when idle.
 */
export function useSyncProgress(tableId: string, optimisticSyncing: boolean): SyncProgressResult {
  const rpc = useRpc();
  const queryClient = useQueryClient();
  const wasSyncingRef = useRef(false);

  const { data } = useQuery({
    queryKey: queryKeys.connectors.syncProgress(tableId),
    queryFn: async (): Promise<SyncProgressResult> => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors['sync-progress'][':id'].$get({
        param: { id: tableId },
      });
      if (!res.ok) return { syncing: false, progress: null };
      const json = await res.json() as { syncing: boolean } & SyncProgressResponse;
      const hasProgress = json.stage != null || (json.recordsFetched != null && json.recordsFetched > 0);
      const progress = hasProgress
        ? { recordsFetched: json.recordsFetched ?? 0, totalRecords: json.totalRecords, stage: json.stage ?? ('fetching' as const) }
        : null;
      return { syncing: json.syncing, progress };
    },
    // Always enabled — fetches on mount to detect in-flight syncs after navigation
    refetchInterval: (query) => {
      const result = query.state.data;
      // Poll fast while optimistically syncing or backend confirms a processing version
      if (optimisticSyncing || result?.syncing) return SYNC_PROGRESS_POLL_MS;
      return false;
    },
    staleTime: 0,
  });

  const result = data ?? { syncing: false, progress: null };

  // When sync finishes (syncing transitions true → false), refresh connectors + tables
  useEffect(() => {
    if (wasSyncingRef.current && !result.syncing) {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
    }
    wasSyncingRef.current = result.syncing;
  }, [result.syncing, queryClient]);

  return result;
}
